import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from '../../../redux/actions/incomeAttributes/incomeAttributes';
import { getLookups } from '../../../redux/actions/lookups/lookups';
import { IncomeAttributeState } from '../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import RevenueTab from './RevenueTab';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion';
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import Total from '../../../components/common/Total/Total';
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject';
import "../PIE.css";
import { useNavigation } from '../../../utils/context/NavigationContext';
import { useFormErrorContext } from '../../../utils/context/FormErrorContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from '../../../redux/actionTypes/actionsTypes';
import PreviousCampaignIncomeAttributes from '../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes';
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';

export default function RevenueContainer(): JSX.Element {
  const noLossTypes = ['htl-income-attributes', 'lle-income-attributes', 'rho-income-attributes', 'glf-income-attributes']
  const { t } = useTranslation(['navigation', 'pie']);
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const menuState = useSelector(selectMenu);
  const tabList = menuState.activeItem.subItems;
  const propertyCode = sessionStorage.getItem("propertyCode") as string


  const [totalIncome, setTotalIncome] = useState<number | undefined>(undefined);
  const [totalLoss, setTotalLoss] = useState<number | undefined>(undefined);
  const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
  const getLossTabName = () => "rentalLoss";
  const lookupState = useSelector(selectLookups);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext()
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem("currentSnapshotId") as string)) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem("currentSnapshotId") as string));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }


  useEffect(() => {
    getIncomeAttributes()
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      const triggerSpectral = isInternal || direction !== "backward"
      dispatch(putIncomeAttributesBySnapshotId(sessionStorage.getItem("currentSnapshotId") as string, {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "property_code": propertyCode,
        "is_internal": isInternal
      }));
    }
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving, profileOverview.profile.profile_type_code, direction, clearErrors, propertyCode, isInternal]);

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem("currentSnapshotId") as string));
    }
  }, [dispatch, saveResultState]);

  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  }

  const findPreviousAttributeData = (attributeCode: string, year: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        incomeAttributesData = (previousSnapshotData.incomeAttribute as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
      }
    }
    return incomeAttributesData;
  }

  const handleTotalIncome = (newAmt: number | undefined | null, name: string, oldAmt: number | undefined | null) => {
    let calculated = false;
    let value = 0;
    const idx = name.lastIndexOf("-");
    let code = "";
    if (idx !== -1) {
      code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (incomeAttributesData) {
        if (lookupType === "ltc-income-attributes" && code === "159") {
          incomeAttributesData["percent"] = na;
        } else {
          incomeAttributesData["value"] = na;
        }
      } else {
        if (lookupType === "ltc-income-attributes" && code === "159") {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
            code: code,
            description: null,
            value: null,
            type: "5",
            percent: na,
            year: null,
          });
        } else {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
            code: code,
            description: null,
            value: na,
            type: "1",
            percent: null,
            year: null,
          });
        }
      }
    }

    if (typeof totalIncome === "number") {
      value = totalIncome;
    }
    if (lookupType === "ltc-income-attributes" && code === "159") {
      if (typeof oldAmt === "number") {
        if (oldAmt / 100 === 1) {
          value = initTotalIncome(getLossTabName(), String(currentCampaignYear), false) || 0;
        } else {
          value = value / (1 - (oldAmt / 100));
        }
        calculated = true;
      }
      if (typeof newAmt === "number") {
        value = value - (value * newAmt / 100);
        calculated = true;
      }
    } else {
      const vacancyAttributesData = findAttributeData("159");
      if (typeof newAmt === "number") {
        if (vacancyAttributesData && vacancyAttributesData.percent) {
          value = value + newAmt - (newAmt * (vacancyAttributesData.percent as number) / 100);
        } else {
          value = value + newAmt;
        }
        calculated = true;
      }
      if (typeof oldAmt === "number") {
        if (vacancyAttributesData && vacancyAttributesData.percent) {
          value = value - oldAmt + (oldAmt * (vacancyAttributesData.percent as number) / 100);
        } else {
          value = value - oldAmt;
        }

        calculated = true;
      }
    }

    if (calculated) {
      setTotalIncome(value);
    }
  }

  const handleTotalLoss = (newAmt: number | undefined | null, name: string, oldAmt: number | undefined | null) => {
    let calculated = false;
    let value = 0;
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (lookupType === "ltc-income-attributes" && code === "159") {
        if (incomeAttributesData) {
          incomeAttributesData["percent"] = na;
        } else {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
            code: code,
            description: null,
            value: null,
            type: "5",
            percent: na,
            year: null,
          });
        }
        return;
      }

      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: null,
          value: na,
          type: "5",
          percent: null,
          year: null,
        });
      }
    }

    if (typeof totalLoss === "number") {
      value = totalLoss;
    }
    if (typeof newAmt === "number") {
      value = value + newAmt;
      calculated = true;
    }
    if (typeof oldAmt === "number") {
      value = value - oldAmt;
      calculated = true;
    }
    if (calculated) {
      setTotalLoss(value);
    }
  }

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: v,
          value: null,
          type: "1",
          percent: null,
          year: null,
        });
      }
    }
  }

  const initTotalIncome = (lossTabName: string, year: string, previousYearTotal: boolean) => {
    let calculated = false;
    let total = 0;
    let vacancyPercent = 0;
    const revenueLookups = lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>
    const keys = Object.keys(revenueLookups);
    keys.forEach((key) => {
      if (key !== lossTabName) {
        const lookupAttributes = revenueLookups[key];
        lookupAttributes.forEach((attribute) => {
          if (attribute.activeFlag === true) {
            const code = attribute.incomeAttributeCode;
            let incomeAttributesData = null
            if (previousYearTotal) {
              incomeAttributesData = findPreviousAttributeData(code, year);
            } else {
              incomeAttributesData = findAttributeData(code);
            }
            if (lookupType === "ltc-income-attributes" && code === "159") {
              if (incomeAttributesData && incomeAttributesData.percent && incomeAttributesData.percent !== 0) {
                vacancyPercent = (incomeAttributesData.percent as number);
              }
            } else {
              if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
                total = total + (incomeAttributesData.value as number);
                calculated = true;
              }
            }
          }
        });
      }
    });
    if (calculated) {
      if (vacancyPercent !== 0) {
        total = total - (total * vacancyPercent / 100);
      }
      if (previousYearTotal) {
        return total;
      } else {
        setTotalIncome(total);
      }
    }
    return undefined;
  }

  const initTotalLoss = (year: string, previousYearTotal: boolean) => {
    let calculated = false;
    let total = 0;

    const revenueLookups = lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>
    const lookupAttributes = revenueLookups[getLossTabName()];
    if (lookupAttributes) {
      lookupAttributes.forEach((attribute) => {
        if (attribute.activeFlag === true) {
          const code = attribute.incomeAttributeCode;
          if (lookupType === "ltc-income-attributes" && code === "159") {
            return;
          }
          let incomeAttributesData = null
          if (previousYearTotal) {
            incomeAttributesData = findPreviousAttributeData(code, year);
          } else {
            incomeAttributesData = findAttributeData(code);
          }
          if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
            total = total + (incomeAttributesData.value as number);
            calculated = true;
          }
        }
      });
    }
    if (calculated) {
      if (previousYearTotal) {
        return total;
      } else {
        setTotalLoss(total);
      }
    }
    return undefined
  }

  useEffect(() => {
    if (lookupState.success && incomeAttributesState.success) {
      if (totalIncome === undefined) {
        initTotalIncome(getLossTabName(), String(currentCampaignYear), false);
      }

      if (totalLoss === undefined) {
        initTotalLoss(String(currentCampaignYear), false);
      }
    }
    // eslint-disable-next-line
  }, [incomeAttributesState.data, incomeAttributesState.success, lookupState.lookups, lookupState.success, lookupType, totalIncome, totalLoss]);

  // const handleTabSelect = (index: number) => {
  //   dispatch(setNextActiveMenuItem({ ...menuState.activeItem, selectedTabIndex: index }));
  // }

  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.success, lookupType]);

  return (
    <div className="full-width">
      <div className='content-container'>
        <ErrorMessages errors={errors} />
        <p className='strong'>{t('revenues.caption', { ns: 'pie' })}</p>
        {
          ((!incomeAttributesState.loading && !previousIncomeAttributesState.loading && !lookupState.loading) &&
            <>
              <div className='accordion-root'>
                <div className={accordionDisplay(isInternal, "accordion")}>
                  {menuState.activeItem.hasTabs && tabList ?
                    <ControlledAccordion providerValue={providerValue}>
                      {tabList.map(tab => {
                        return <AccordionItem
                          header={t(tab.name, { ns: 'navigation' })}
                          key={tab.name}
                          itemKey={tab.name}
                        >
                          <RevenueTab handleTotalIncome={handleTotalIncome}
                            handleTotalLoss={handleTotalLoss}
                            handleCommentChange={handleCommentChange}
                            name={tab.name}
                            lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)[tab.name] : []}
                            incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                            lookupType={lookupType} />
                        </AccordionItem>
                      })}
                    </ControlledAccordion>
                    :
                    <ControlledAccordion providerValue={providerValue}>
                      <AccordionItem header={t(menuState.activeItem.name, { ns: 'navigation' })}>
                        <RevenueTab handleTotalIncome={handleTotalIncome}
                          handleTotalLoss={handleTotalLoss}
                          handleCommentChange={handleCommentChange}
                          name={menuState.activeItem.name}
                          lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)[menuState.activeItem.name] : []}
                          incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                          lookupType={lookupType} />
                      </AccordionItem>
                    </ControlledAccordion>
                  }
                </div>
                {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes name={menuState.activeItem.name} tabComponent='revenue' tabList={tabList} lookupType={lookupType} forcedTab year={String(currentCampaignYear - 1)} /> : <></>}
                {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes name={menuState.activeItem.name} tabComponent='revenue' tabList={tabList} lookupType={lookupType} forcedTab year={String(currentCampaignYear - 2)} /> : <></>}
              </div>
            </>
          )
        }
      </div>
      <div className='total-container'>
        <div className="accordion-root">
          <div className={accordionDisplay(isInternal, "accordion")}>
            <Total text={t('revenues.totalRevenueIncome', { ns: 'pie' })} value={totalIncome} id={'totalIncome'} label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
            {!noLossTypes.includes(lookupType) &&
              <Total text={t('revenues.totalRevenueLoss', { ns: 'pie' })} value={totalLoss}
                className='secondary' space={false} id={'totalLoss'} label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
            }
          </div>
          {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            <Total text={t('revenues.totalRevenueIncome', { ns: 'pie' })} value={initTotalIncome(getLossTabName(), String(currentCampaignYear - 1), true)} id={'totalIncome'} />
            {!noLossTypes.includes(lookupType) &&
              <Total text={t('revenues.totalRevenueLoss', { ns: 'pie' })} value={initTotalLoss(String(currentCampaignYear - 1), true)}
                className='secondary' space={false} id={'totalLoss'} />
            }
          </div> : <></>}
          {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            <Total text={t('revenues.totalRevenueIncome', { ns: 'pie' })} value={initTotalIncome(getLossTabName(), String(currentCampaignYear - 2), true)} id={'totalIncome'} />
            {!noLossTypes.includes(lookupType) &&
              <Total text={t('revenues.totalRevenueLoss', { ns: 'pie' })} value={initTotalLoss(String(currentCampaignYear - 2), true)}
                className='secondary' space={false} id={'totalLoss'} />
            }
          </div> : <></>}
        </div>
      </div>
    </div>);
}